// Read-only preview of a workflow's input contract — answers
// "what inputs does this workflow expect?" at a glance, without
// forcing the user to read the JSON Schema textarea below it.
// (DEV-16143.)
import { type CSSProperties } from 'react';

import { parseInputSchemaFields, type ISchemaField } from '../../lib/json-schema';

const wrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
};

const emptyState: CSSProperties = {
  padding: 'var(--sp-3)',
  background: 'var(--surface-2)',
  border: '1px dashed var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-secondary)',
};

const fieldRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  alignItems: 'baseline',
  gap: 'var(--sp-2)',
  padding: 'var(--sp-2) var(--sp-3)',
  background: 'var(--surface-2)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
};

const nameStyle: CSSProperties = {
  fontFamily: 'var(--font-mono, monospace)',
  fontSize: 'var(--fs-sm)',
  color: 'var(--text-primary)',
};

const requiredTag: CSSProperties = {
  fontSize: 10,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--status-danger)',
  marginLeft: 'var(--sp-1)',
};

const typeChip: CSSProperties = {
  fontFamily: 'var(--font-mono, monospace)',
  fontSize: 10,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--text-secondary)',
  background: 'var(--surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-xs)',
  padding: '2px 6px',
  whiteSpace: 'nowrap',
};

const descStyle: CSSProperties = {
  gridColumn: '1 / -1',
  margin: 0,
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-secondary)',
  lineHeight: 1.4,
};

/**
 * Render the parsed field list for an input schema. `schema` is the raw
 * JSON Schema value (from `/v1/workflows/:id/input-schema`); a `null`
 * value means the workflow has no published contract.
 *
 * Schemas the parser can't flatten (non-object roots, missing
 * `properties`, etc.) get the same "free-form input" empty state — the
 * runner doesn't enforce structure on those either, so the message is
 * accurate.
 */
export function WorkflowInputSchemaView({ schema }: { schema: unknown }) {
  if (schema === null || schema === undefined) {
    return (
      <div style={emptyState}>
        No input contract published — this workflow accepts free-form input.
      </div>
    );
  }
  const fields = parseInputSchemaFields(schema);
  if (fields === null) {
    return (
      <div style={emptyState}>
        The schema is published but isn’t a flat <code>{'{ type: "object" }'}</code> with{' '}
        <code>properties</code> — see the JSON below for the raw definition.
      </div>
    );
  }
  if (fields.length === 0) {
    return (
      <div style={emptyState}>
        The schema is published but defines no properties — the workflow accepts an empty object.
      </div>
    );
  }
  return (
    <div style={wrap} role="list" aria-label="Workflow input fields">
      {fields.map((field) => (
        <FieldRow key={field.name} field={field} />
      ))}
    </div>
  );
}

function FieldRow({ field }: { field: ISchemaField }) {
  return (
    <div style={fieldRow} role="listitem">
      <span style={nameStyle}>
        {field.name}
        {field.required ? (
          <span style={requiredTag} aria-label="required">
            required
          </span>
        ) : null}
      </span>
      <span style={typeChip} aria-label={`type ${field.type}`}>
        {field.type}
      </span>
      {field.description ? <p style={descStyle}>{field.description}</p> : null}
    </div>
  );
}
